import React from 'react'
import { Avatar, Box, Checkbox, Chip, FormControlLabel, Grid, Stack, Typography, alpha } from '@mui/material'
import {
  Business as BusinessIcon,
  MailOutline as MailOutlineIcon,
  Person as PersonIcon,
} from '@mui/icons-material'
import AdminFormSection from './fields/AdminFormSection'
import NoticeBar, { NoticeSeverity } from '../../shared/NoticeBar'
import { deriveCountryCode, getCountryChipSx } from '../../../utils/countryChips'
import { poolwerxColors } from '../../../theme/colors'

/**
 * Shared confirm-dialog body for FP user actions: a stacked pair of summary
 * cards (the standard admin `AdminFormSection card` panel + teal heading) - the
 * subject (a user or an invited email) on top, the franchise partner(s) below -
 * with an optional `NoticeBar` callout and footer.
 *
 * Each FP shows its market chip (AU/NZ/US) rather than the fp_code. With
 * `selectable` the FP card is a scrollable checklist; with `destructiveUnchecked`
 * (the "update access" flow) a *ticked* row keeps access and an *unticked* row
 * is flagged red as Removing. Used by the grant / invite / remove-user /
 * delete-invitation confirms so they all read the same way.
 */

const KNOWN_COUNTRIES = new Set(['AU', 'NZ', 'US'])

const CountryChip: React.FC<{ fpCode: string }> = ({ fpCode }) => {
  const country = deriveCountryCode(fpCode)
  if (!KNOWN_COUNTRIES.has(country)) return null
  return (
    <Chip
      label={country}
      size="small"
      variant="outlined"
      sx={{
        ...getCountryChipSx(country),
        height: 20,
        fontSize: '0.6875rem',
        flexShrink: 0,
        '& .MuiChip-label': { px: 0.75, fontWeight: 600 },
      }}
    />
  )
}

const SubjectAvatar: React.FC<{ kind: 'user' | 'invite' }> = ({ kind }) => {
  const Icon = kind === 'invite' ? MailOutlineIcon : PersonIcon
  return (
    <Avatar
      sx={{
        width: 32,
        height: 32,
        bgcolor: alpha(poolwerxColors.teal, 0.12),
        color: poolwerxColors.teal,
      }}
    >
      <Icon fontSize="small" />
    </Avatar>
  )
}

/** One FP line: building icon + name + market chip. When `removing`, the name
 * is struck through and the row is tinted orange (see the wrapper below). */
const FpLine: React.FC<{ name: string; fpCode: string; removing?: boolean }> = ({
  name,
  fpCode,
  removing,
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, width: '100%' }}>
    <BusinessIcon
      sx={(theme) => ({
        fontSize: 18,
        color: removing ? theme.palette.warning.main : poolwerxColors.teal,
        flexShrink: 0,
      })}
    />
    <Typography
      variant="body2"
      noWrap
      sx={{
        fontWeight: 500,
        flex: 1,
        minWidth: 0,
        ...(removing && { textDecoration: 'line-through', color: 'text.secondary' }),
      }}
    >
      {name}
    </Typography>
    <CountryChip fpCode={fpCode} />
  </Box>
)

export interface ConfirmFpEntry {
  /** Required only for selectable rows (drives the checkbox key + toggle). */
  fpId?: number
  name: string
  fpCode: string
  selected?: boolean
  onToggle?: () => void
}

interface ConfirmUserFpBodyProps {
  subjectKind?: 'user' | 'invite'
  subjectLabel?: string
  subjectPrimary: string
  subjectSecondary?: string
  fpLabel?: string
  fps: ConfirmFpEntry[]
  selectable?: boolean
  /** "Update access" mode: ticked = keep, unticked = Removing (red highlight). */
  destructiveUnchecked?: boolean
  disabled?: boolean
  /** Light callout below the cards (info for grant/invite, warning for destructive). */
  notice?: { severity?: NoticeSeverity; content: React.ReactNode }
  /** Extra controls rendered full-width below the callout (e.g. delete checkbox). */
  footer?: React.ReactNode
}

const ConfirmUserFpBody: React.FC<ConfirmUserFpBodyProps> = ({
  subjectKind = 'user',
  subjectLabel = 'User',
  subjectPrimary,
  subjectSecondary,
  fpLabel = 'Franchise Partner',
  fps,
  selectable = false,
  destructiveUnchecked = false,
  disabled,
  notice,
  footer,
}) => (
  <Grid container spacing={1.5}>
    <AdminFormSection card dense title={subjectLabel}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
        <SubjectAvatar kind={subjectKind} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
            {subjectPrimary}
          </Typography>
          {subjectSecondary && (
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }} noWrap>
              {subjectSecondary}
            </Typography>
          )}
        </Box>
      </Box>
    </AdminFormSection>

    <AdminFormSection card dense title={fpLabel}>
      {selectable ? (
        <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
          <Stack spacing={0.5}>
            {fps.map((fp, i) => {
              const removing = destructiveUnchecked && !fp.selected
              return (
                <Box
                  key={fp.fpId ?? i}
                  sx={(theme) => ({
                    borderRadius: 1,
                    px: 0.75,
                    border: '1px solid',
                    borderColor: removing
                      ? alpha(theme.palette.warning.main, 0.4)
                      : 'transparent',
                    bgcolor: removing ? alpha(theme.palette.warning.main, 0.12) : 'transparent',
                  })}
                >
                  <FormControlLabel
                    sx={{
                      mx: 0,
                      width: '100%',
                      '& .MuiFormControlLabel-label': { flex: 1, minWidth: 0 },
                    }}
                    control={
                      <Checkbox
                        size="small"
                        checked={!!fp.selected}
                        onChange={fp.onToggle}
                        disabled={disabled}
                      />
                    }
                    label={<FpLine name={fp.name} fpCode={fp.fpCode} removing={removing} />}
                  />
                </Box>
              )
            })}
          </Stack>
        </Box>
      ) : (
        <Stack spacing={0.75}>
          {fps.map((fp, i) => (
            <FpLine key={fp.fpId ?? i} name={fp.name} fpCode={fp.fpCode} />
          ))}
        </Stack>
      )}
    </AdminFormSection>

    {notice && (
      <Grid item xs={12}>
        <NoticeBar severity={notice.severity ?? 'info'}>{notice.content}</NoticeBar>
      </Grid>
    )}
    {footer && (
      <Grid item xs={12}>
        {footer}
      </Grid>
    )}
  </Grid>
)

export default ConfirmUserFpBody
